import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DivisionUsersService } from '../services/division-users-service';
import { MatchIdentifierService } from '../services/match-identifier-service';
import httpClient from '../services/http-client';
import { 
  UserMatchIdentifier,
  CreateUserMatchIdentifierRequest,
  UpdateUserMatchIdentifierRequest,
  BatchMatchIdentifierRequest,
  MatchIdentifiersListResponse,
  TestUserMatchIdentifierRequest,
  TestUserMatchIdentifierResponse
} from '../types';

const divisionUsersService = new DivisionUsersService(httpClient);
const matchIdentifierService = new MatchIdentifierService(httpClient);

export const useUserMatchIdentifiers = (divisionId: string, userId: string) => {
  return useQuery({
    queryKey: ['user-match-identifiers', divisionId, userId],
    queryFn: () => divisionUsersService.getUserMatchIdentifiers(divisionId, userId),
    enabled: !!divisionId && !!userId,
  });
};

export const useAvailableMatchIdentifiers = (divisionId: string) => {
  return useQuery({
    queryKey: ['available-match-identifiers', divisionId],
    queryFn: (): Promise<MatchIdentifiersListResponse> => 
      matchIdentifierService.getMatchIdentifiers(divisionId, { active: true }),
    enabled: !!divisionId,
  });
};

export const useAssignUserMatchIdentifier = (divisionId: string, userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserMatchIdentifierRequest) =>
      divisionUsersService.assignMatchIdentifier(divisionId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-match-identifiers', divisionId, userId] });
      queryClient.invalidateQueries({ queryKey: ['division-users', divisionId] });
      console.log('Match identifier assigned successfully');
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message || 'Failed to assign match identifier';
      
      if (status === 401) {
        console.error('Authentication required. Please log in.');
      } else if (status === 403) {
        console.error('Access denied. Admin or DivisionAdmin role required.');
      } else if (status === 404) {
        console.error('Resource not found. Please check the division and user exist.');
      } else {
        console.error(errorMessage);
      }
    },
  });
};

export const useUpdateUserMatchIdentifier = (divisionId: string, userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userMatchIdentifierId, data }: { 
      userMatchIdentifierId: string; 
      data: UpdateUserMatchIdentifierRequest 
    }) =>
      divisionUsersService.updateUserMatchIdentifier(divisionId, userId, userMatchIdentifierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-match-identifiers', divisionId, userId] });
      queryClient.invalidateQueries({ queryKey: ['division-users', divisionId] });
      console.log('Match identifier updated successfully');
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message || 'Failed to update match identifier';
      
      if (status === 401) {
        console.error('Authentication required. Please log in.');
      } else if (status === 403) {
        console.error('Access denied. Admin or DivisionAdmin role required.');
      } else if (status === 404) {
        console.error('Match identifier not found.');
      } else {
        console.error(errorMessage);
      }
    },
  });
};

export const useDeleteUserMatchIdentifier = (divisionId: string, userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userMatchIdentifierId: string) =>
      divisionUsersService.removeUserMatchIdentifier(divisionId, userId, userMatchIdentifierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-match-identifiers', divisionId, userId] });
      queryClient.invalidateQueries({ queryKey: ['division-users', divisionId] });
      console.log('Match identifier removed successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to remove match identifier';
      console.error(errorMessage);
    },
  });
};

export const useBatchAssignMatchIdentifiers = (divisionId: string, userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BatchMatchIdentifierRequest) =>
      divisionUsersService.batchAssignMatchIdentifiers(divisionId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-match-identifiers', divisionId, userId] });
      queryClient.invalidateQueries({ queryKey: ['division-users', divisionId] });
      console.log('Match identifiers assigned successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to assign match identifiers';
      console.error(errorMessage);
    },
  });
};

export const useTestUserMatchIdentifier = (divisionId: string, userId: string) => {
  return useMutation({
    mutationFn: (request: TestUserMatchIdentifierRequest) =>
      divisionUsersService.testUserMatchIdentifiers(divisionId, userId, request),
    onSuccess: (result: TestUserMatchIdentifierResponse) => {
      const matchRate = result.summary.matchRate;
      if (matchRate > 0.5) {
        console.log(`Match identifier test passed with ${(matchRate * 100).toFixed(1)}% match rate`);
      } else {
        console.log(`Match identifier test had low success rate: ${(matchRate * 100).toFixed(1)}%`);
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to test match identifier';
      console.error(errorMessage);
    },
  });
};
